export function polyfill(): void {
    try {
        polyfillArrayIncludes();
        polyfillArrayFill();
        polyfillCodePointAt();
        polyfillFromCodePoint();
    } catch (e) {
        console.error(e);
    }
}

// IMPORTANT - it has to be called immediately because webpack evaluates other packages while loading this one.
// Calling it here will give us a confidence it is always polyfilled
polyfill();

declare global {
    interface Array<T> {
        includes(searchElement: T): boolean;
    }
}

/**
 * Includes is ES2016 only
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes
 */
function polyfillArrayIncludes(): void {
    if (!Array.prototype.includes) {
        Object.defineProperty(Array.prototype, 'includes', {
            value: function (searchElement: any, fromIndex: number): boolean {
                // 1. Let O be ? ToObject(this value).
                if (this == null) {
                    throw new TypeError('"this" is null or not defined');
                }

                const o = Object(this);

                // 2. Let len be ? ToLength(? Get(O, "length")).
                const len = o.length >>> 0;

                // 3. If len is 0, return false.
                if (len === 0) {
                    return false;
                }

                // 4. Let n be ? ToInteger(fromIndex).
                //    (If fromIndex is undefined, this step produces the value 0.)
                const n = fromIndex | 0;

                // 5. If n ≥ 0, then
                //  a. Let k be n.
                // 6. Else n < 0,
                //  a. Let k be len + n.
                //  b. If k < 0, let k be 0.
                let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);

                function sameValueZero<T>(x: T, y: T): boolean {
                    return (
                        x === y ||
                        (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y))
                    );
                }

                // 7. Repeat, while k < len
                while (k < len) {
                    // a. Let elementK be the result of ? Get(O, ! ToString(k)).
                    // b. If SameValueZero(searchElement, elementK) is true, return true.
                    // c. Increase k by 1.
                    if (sameValueZero(o[k], searchElement)) {
                        return true;
                    }
                    k++;
                }

                // 8. Return false
                return false;
            },
        });
    }
}

/**
 * Array.fill is not supported in IE
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/fill
 */
function polyfillArrayFill(): void {
    if (!(Array.prototype as any).fill) {
        Object.defineProperty(Array.prototype, 'fill', {
            value: function (value: any, start: number = 0, end?: number): any {
                // Steps 1-2.
                if (this == null) {
                    throw new TypeError('this is null or not defined');
                }

                const O = Object(this);

                // Steps 3-5.
                const len = O.length >>> 0;

                // Steps 6-7.
                const relativeStart = start >> 0;

                // Step 8.
                let k =
                    relativeStart < 0
                        ? Math.max(len + relativeStart, 0)
                        : Math.min(relativeStart, len);

                // Steps 9-10.
                const relativeEnd = end === undefined ? len : end >> 0;

                // Step 11.
                const final =
                    relativeEnd < 0 ? Math.max(len + relativeEnd, 0) : Math.min(relativeEnd, len);

                // Step 12.
                while (k < final) {
                    O[k] = value;
                    k++;
                }

                // Step 13.
                return O;
            },
        });
    }
}

/**
 * The codePointAt() method returns a non-negative integer that is the Unicode code point value.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/codePointAt
 */

function polyfillCodePointAt(): void {
    if (!String.prototype.codePointAt) {
        (function (): void {
            'use strict'; // needed to support `apply`/`call` with `undefined`/`null`
            const defineProperty = (function (): any {
                let result;
                // IE 8 only supports `Object.defineProperty` on DOM elements
                try {
                    const object = {};
                    const $defineProperty = Object.defineProperty;
                    result = $defineProperty(object, object as any, object) && $defineProperty;
                } catch (error) {}
                return result;
            })();
            const codePointAt = function (this: unknown, position: number): number {
                if (this == null) {
                    throw TypeError();
                }
                const string = String(this);
                const size = string.length;
                // `ToInteger`
                let index = position ? Number(position) : 0;
                if (index !== index) {
                    // better `isNaN`
                    index = 0;
                }
                // Account for out-of-bounds indices:
                if (index < 0 || index >= size) {
                    return undefined;
                }
                // Get the first code unit
                const first = string.charCodeAt(index);
                let second;
                if (
                    // check if it’s the start of a surrogate pair
                    first >= 0xd800 &&
                    first <= 0xdbff && // high surrogate
                    size > index + 1 // there is a next code unit
                ) {
                    second = string.charCodeAt(index + 1);
                    if (second >= 0xdc00 && second <= 0xdfff) {
                        // low surrogate
                        return (first - 0xd800) * 0x400 + second - 0xdc00 + 0x10000;
                    }
                }
                return first;
            };
            if (defineProperty) {
                defineProperty(String.prototype, 'codePointAt', {
                    value: codePointAt,
                    configurable: true,
                    writable: true,
                });
            } else {
                String.prototype.codePointAt = codePointAt;
            }
        })();
    }
}

/**
 * The static String.fromCodePoint() method returns a string created by using the specified sequence of code points.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/fromCodePoint
 */
function polyfillFromCodePoint(): void {
    if (!(String as any).fromCodePoint) {
        (function (): void {
            const defineProperty = (function (): any {
                let result;
                // IE 8 only supports `Object.defineProperty` on DOM elements
                try {
                    const object = {};
                    const $defineProperty = Object.defineProperty;
                    result = $defineProperty(object, object as any, object) && $defineProperty;
                } catch (error) {}
                return result;
            })();
            const stringFromCharCode = String.fromCharCode;
            const floor = Math.floor;
            const fromCodePoint = function (_: any): string {
                const MAX_SIZE = 0x4000;
                const codeUnits = [];
                let highSurrogate;
                let lowSurrogate;
                let index = -1;
                const length = arguments.length;
                if (!length) {
                    return '';
                }
                let result = '';
                while (++index < length) {
                    let codePoint = Number(arguments[index]);
                    if (
                        !isFinite(codePoint) || // `NaN`, `+Infinity`, or `-Infinity`
                        codePoint < 0 || // not a valid Unicode code point
                        codePoint > 0x10ffff || // not a valid Unicode code point
                        floor(codePoint) !== codePoint // not an integer
                    ) {
                        throw RangeError('Invalid code point: ' + codePoint);
                    }
                    if (codePoint <= 0xffff) {
                        // BMP code point
                        codeUnits.push(codePoint);
                    } else {
                        codePoint -= 0x10000;
                        highSurrogate = (codePoint >> 10) + 0xd800;
                        lowSurrogate = (codePoint % 0x400) + 0xdc00;
                        codeUnits.push(highSurrogate, lowSurrogate);
                    }
                    if (index + 1 === length || codeUnits.length > MAX_SIZE) {
                        result += stringFromCharCode.apply(null, codeUnits);
                        codeUnits.length = 0;
                    }
                }
                return result;
            };
            if (defineProperty) {
                defineProperty(String, 'fromCodePoint', {
                    value: fromCodePoint,
                    configurable: true,
                    writable: true,
                });
            } else {
                (String as any).fromCodePoint = fromCodePoint;
            }
        })();
    }
}
